import { Box, Chip, Collapse, TextField, Typography } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import React from 'react';
import { Palette } from 'Palette';
import { RADIUS } from 'DesignTokens';
import type { AdminFundUpdateEntry } from '../../models/FundUpdateEntry';
import { SECONDARY_BUTTON_HOVER_SX, StyledButton } from '../styles/StyledButtons';
import HighlightCardPreview from './HighlightCardPreview';
import HighlightGenerateButtons from './HighlightGenerateButtons';
import HighlightImagePicker from './HighlightImagePicker';
import type { HighlightEditState, HighlightSuggestion } from './fundUpdatesAdmin.types';
import { extractImageUrlsFromContent, inferHighlightFromContent } from './fundUpdatesAdminUtils';

export interface FundHighlightSectionProps {
  update: AdminFundUpdateEntry;
  fundName: string;
  canEditCard: boolean;
  isOpen: boolean;
  isEditing: boolean;
  isGenerating: boolean;
  isGeneratingPro: boolean;
  isCmsBusy: boolean;
  isSavingForGenerate: boolean;
  isSavingForGeneratePro: boolean;
  suggestion: HighlightSuggestion | null;
  editState: HighlightEditState;
  refinementPrompt: string;
  onToggleOpen: () => void;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onEditChange: (field: keyof HighlightEditState, value: string) => void;
  onSave: () => void;
  onClear: () => void;
  onGenerate: (useProModel: boolean, refinementPrompt?: string) => void;
  onAcceptSuggestion: () => void;
  onRejectSuggestion: () => void;
  onRefinementPromptChange: (value: string) => void;
  onSuggestionImageSelect: (imageUrl: string) => void;
}

export default function FundHighlightSection({
  update,
  fundName,
  canEditCard,
  isOpen,
  isEditing,
  isGenerating,
  isGeneratingPro,
  isCmsBusy,
  isSavingForGenerate,
  isSavingForGeneratePro,
  suggestion,
  editState,
  refinementPrompt,
  onToggleOpen,
  onStartEdit,
  onCancelEdit,
  onEditChange,
  onSave,
  onClear,
  onGenerate,
  onAcceptSuggestion,
  onRejectSuggestion,
  onRefinementPromptChange,
  onSuggestionImageSelect,
}: FundHighlightSectionProps) {
  const hasCard = !!update.updateCard;
  const card = update.updateCard;
  const hasContent = (update.content ?? []).length > 0;
  const inferred = inferHighlightFromContent(update.content ?? []);
  const previewTitle = hasCard ? card!.properties.title : inferred.title;
  const previewSummary = hasCard ? card!.properties.summary : inferred.summary;
  const previewImage = hasCard ? (card!.properties.imageUrl ?? null) : inferred.imageUrl;
  const previewDate = update.publishedAt || update.updateDate || null;

  const contentImages = extractImageUrlsFromContent(update.content ?? []);
  const availableImages = [...new Set([...contentImages, ...(editState.availableImages ?? [])])];

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: Palette.default.border,
        borderRadius: RADIUS.section,
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          bgcolor: Palette.default.background,
          borderBottom: `1px solid ${Palette.default.border}`,
          cursor: 'pointer',
        }}
        onClick={onToggleOpen}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
            Fund Highlight Card
          </Typography>
          {hasCard ? (
            <Chip
              label="Custom"
              size="small"
              color="success"
              variant="outlined"
              sx={{ fontSize: '0.65rem', height: 20 }}
            />
          ) : (
            <Chip
              label="Auto-inferred"
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.65rem', height: 20 }}
            />
          )}
        </Box>
        {isOpen ? <ExpandLess /> : <ExpandMore />}
      </Box>

      <Collapse in={isOpen}>
        <Box sx={{ px: 2, pb: 2, pt: 2 }}>
          {!isEditing && !suggestion && (
            <Box>
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', display: 'block', mb: 1 }}
              >
                Preview (as shown in app)
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <HighlightCardPreview
                  date={previewDate}
                  fundName={fundName}
                  title={previewTitle}
                  summary={previewSummary}
                  imageUrl={previewImage}
                  isInferred={!hasCard}
                />
                {canEditCard && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <StyledButton
                      noTopSpacing
                      buttonVariant="inactiveOrCancel"
                      sx={SECONDARY_BUTTON_HOVER_SX}
                      disabled={isCmsBusy}
                      onClick={onStartEdit}
                    >
                      {hasCard ? 'Edit' : 'Create custom card'}
                    </StyledButton>
                    <HighlightGenerateButtons
                      hasContent={hasContent}
                      isGenerating={isGenerating}
                      isGeneratingPro={isGeneratingPro}
                      isCmsBusy={isCmsBusy}
                      isSavingForGenerate={isSavingForGenerate}
                      isSavingForGeneratePro={isSavingForGeneratePro}
                      onGenerateFast={() => onGenerate(false)}
                      onGeneratePro={() => onGenerate(true)}
                    />
                    {hasCard && (
                      <StyledButton
                        noTopSpacing
                        buttonVariant="warning"
                        disabled={isCmsBusy}
                        onClick={onClear}
                      >
                        Remove custom card
                      </StyledButton>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {canEditCard && suggestion && !isEditing && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                AI Generated Suggestion
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <HighlightCardPreview
                  date={previewDate}
                  fundName={fundName}
                  title={suggestion.title}
                  summary={suggestion.summary}
                  imageUrl={suggestion.imageUrl}
                  isInferred={false}
                />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <StyledButton noTopSpacing buttonVariant="default" onClick={onAcceptSuggestion}>
                    Accept
                  </StyledButton>
                  <StyledButton
                    noTopSpacing
                    buttonVariant="inactiveOrCancel"
                    sx={SECONDARY_BUTTON_HOVER_SX}
                    onClick={onRejectSuggestion}
                  >
                    Reject
                  </StyledButton>
                </Box>
              </Box>
              {suggestion.availableImages.length > 1 && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}
                  >
                    Use a different image:
                  </Typography>
                  <HighlightImagePicker
                    images={suggestion.availableImages}
                    selectedUrl={suggestion.imageUrl ?? ''}
                    onSelect={onSuggestionImageSelect}
                  />
                </Box>
              )}
            </Box>
          )}

          {canEditCard && isEditing && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Edit Highlight Card
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flex: '1 1 280px', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <TextField
                    label="Title"
                    size="small"
                    fullWidth
                    value={editState.title}
                    onChange={(e) => onEditChange('title', e.target.value)}
                  />
                  <TextField
                    label="Summary (25 words or less)"
                    size="small"
                    fullWidth
                    multiline
                    rows={2}
                    value={editState.summary}
                    onChange={(e) => onEditChange('summary', e.target.value)}
                    helperText={`${editState.summary.trim().split(/\s+/).filter(Boolean).length} words`}
                  />
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}
                    >
                      Image:
                    </Typography>
                    <HighlightImagePicker
                      images={availableImages}
                      selectedUrl={editState.imageUrl}
                      onSelect={(url) => onEditChange('imageUrl', url)}
                      showNoImageOption
                    />
                  </Box>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}
                  >
                    Live preview:
                  </Typography>
                  <HighlightCardPreview
                    date={previewDate}
                    fundName={fundName}
                    title={editState.title}
                    summary={editState.summary}
                    imageUrl={editState.imageUrl || null}
                    isInferred={false}
                  />
                </Box>
              </Box>
              <Box
                sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}
              >
                <TextField
                  label="Refinement prompt (optional)"
                  size="small"
                  placeholder="e.g. Focus on the distribution rate"
                  value={refinementPrompt}
                  onChange={(e) => onRefinementPromptChange(e.target.value)}
                  sx={{ flex: '1 1 200px' }}
                />
                <HighlightGenerateButtons
                  hasContent={hasContent}
                  isGenerating={isGenerating}
                  isGeneratingPro={isGeneratingPro}
                  isCmsBusy={isCmsBusy}
                  isSavingForGenerate={isSavingForGenerate}
                  isSavingForGeneratePro={isSavingForGeneratePro}
                  onGenerateFast={() => onGenerate(false, refinementPrompt)}
                  onGeneratePro={() => onGenerate(true, refinementPrompt)}
                  fastLabel="Regenerate (Fast)"
                  proLabel="Regenerate (Thinking)"
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <StyledButton
                  noTopSpacing
                  buttonVariant="default"
                  onClick={onSave}
                  disabled={!editState.title.trim()}
                >
                  Done
                </StyledButton>
                <StyledButton
                  noTopSpacing
                  buttonVariant="inactiveOrCancel"
                  sx={SECONDARY_BUTTON_HOVER_SX}
                  onClick={onCancelEdit}
                >
                  Cancel
                </StyledButton>
              </Box>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}
